import moment from 'moment';
import { call, put, putResolve, select, takeEvery } from 'typed-redux-saga';

import { walletsAPI } from '@/api';
import { makeSelectInitialData } from '@/model/auth/selectors';
import * as global from '@/model/global/actions';
import { withGlobalLock } from '@/model/global/utils';
import * as withdrawal from '@/model/withdrawal/actions';
import { onWithdrawalErrorMessage } from '@/model/withdrawal/utils';
import { ActionWithPayload, takeEveryTypesafe } from '@/utils/redux';

import * as actions from './actions';
import { makeSelectLastReload, makeSelectTransactions, makeSelectTransactionsFilter } from './selectors';
import { AnyTransactionPeriod, NAMESPACE, TransactionPeriod } from './types';

const transactionFilterToPeriod = (period: AnyTransactionPeriod): { dateFrom?: Date; dateTo?: Date } => {
  switch (period.type) {
    case TransactionPeriod.lastWeek:
      return {
        dateFrom: moment()
          .subtract(1, 'week')
          .toDate(),
        dateTo: moment().toDate(),
      };
    case TransactionPeriod.lastMonth:
      return {
        dateFrom: moment()
          .subtract(1, 'month')
          .toDate(),
        dateTo: moment().toDate(),
      };
    case TransactionPeriod.last3Month:
      return {
        dateFrom: moment()
          .subtract(3, 'month')
          .toDate(),
        dateTo: moment().toDate(),
      };
    case TransactionPeriod.custom:
      return { dateFrom: period.dateFrom, dateTo: period.dateTo };
    default:
      return {};
  }
};

function* init(): Generator {
  try {
    let state: actions.StoreInitActionPayload = { wallets: { all: [] } };
    const initial = yield* select(makeSelectInitialData());

    try {
      state = { ...state, wallets: { all: yield* call(walletsAPI.queryWallets), selected: initial?.accountId } };
    } catch (e) {
      // do nothing
    }
    yield* putResolve(actions.storeInitialized(state).pure);
  } finally {
    yield put(actions.notifyInitializationFinished().pure);
  }
}

let fetching = false;

function* requestTransactions(payload: ActionWithPayload<actions.RequestTransactionsActionPayload>): Generator {
  if (fetching) {
    return [];
  }
  const filter = yield* select(makeSelectTransactionsFilter());
  try {
    fetching = true;
    const transactions = yield* select(makeSelectTransactions());
    const page = (transactions?.lastPage || 0) + 1;
    const data = yield* call(
      walletsAPI.queryTransactions,
      {
        ...transactionFilterToPeriod(filter),
        // FIXME: different types?
        // type: type !== 'all' ? type : undefined,
      },
      {
        page,
        perPage: 25,
      },
    );
    const hasMore = data.length === 25;
    yield* putResolve(actions.storeTransactions({ data, lastPage: page, hasMore }).pure);
    return data;
  } finally {
    fetching = false;
  }
}

function* reloadBalance({ payload: { force } }: ActionWithPayload<actions.ReloadBalanceActionPayload>): Generator {
  const lastReloadDate = yield* select(makeSelectLastReload('balance'));
  // prettier-ignore
  // prettier: FIXME: https://github.com/prettier/prettier/pull/7111
  if (force || !lastReloadDate || moment(lastReloadDate)
    .add(10, 'seconds')
    .isBefore()) {
    const all = yield* call(walletsAPI.queryWallets);
    const initial = yield* select(makeSelectInitialData());
    yield* putResolve(actions.storeWallets({ all, selected: initial?.accountId }).pure);
    yield* put(actions.storeLastReload({ type: 'balance', value: new Date() }).pure);
  }
}

function* reloadTransactions({
  payload: { force },
}: ActionWithPayload<actions.ReloadTransactionsActionPayload>): Generator {
  const lastReloadDate = yield* select(makeSelectLastReload('transactions'));
  // prettier-ignore
  // prettier: FIXME: https://github.com/prettier/prettier/pull/7111
  if (force || !lastReloadDate || moment(lastReloadDate)
    .add(10, 'seconds')
    .isBefore()) {
    const filter = yield* select(makeSelectTransactionsFilter());
    const data = yield* call(
      walletsAPI.queryTransactions,
      {
        ...transactionFilterToPeriod(filter),
        // FIXME: different types?
        // type: type !== 'all' ? type : undefined,
      },
      {
        page: 1,
        perPage: 25,
      },
    );
    const hasMore = data.length === 25;
    const oldTransactions = yield* select(makeSelectTransactions());
    if (force
      || !oldTransactions
      || oldTransactions?.data[0]?.cid !== data[0]?.cid
      || oldTransactions?.data[0]?.uid !== data[0]?.uid) {
      yield* putResolve(actions.storeTransactions({ data, lastPage: 1, hasMore }).pure);
    }
    yield* put(actions.storeLastReload({
      type: 'transactions',
      value: new Date(),
    }).pure);
  }
}

function* logout(): Generator {
  yield* put(actions.storeLogout().pure);
}

function* clearTransactions(): Generator {
  yield* putResolve(actions.storeClearTransactions().pure);
}

export default function* rootSaga(): Generator {
  yield* takeEvery(withdrawal.notifyWithdrawalFinished.type, clearTransactions);
  yield* takeEveryTypesafe(actions.init, withGlobalLock(init, 'common.messages.operations.init'));
  yield* takeEveryTypesafe(
    actions.requestTransactions,
    withGlobalLock(requestTransactions, 'demo.messages.operations.transactions.request'),
    onWithdrawalErrorMessage(NAMESPACE, 'demo.errors.operations.transactions.request'),
  );
  yield* takeEveryTypesafe(actions.reloadBalance, reloadBalance);
  yield* takeEveryTypesafe(actions.reloadTransactions, reloadTransactions);
  yield* takeEvery(global.notifyLogout.type, withGlobalLock(logout, 'common.messages.operations.logout'));
}
